/**
 * Account Payment Methods Page
 * Manage saved payment methods - fetches real data from API
 */
import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plus, CreditCard, AlertCircle, Loader2, CheckCircle } from 'lucide-react';
import AccountHeader from '../../components/account/AccountHeader';
import PaymentMethodCard from '../../components/account/PaymentMethodCard';
import AddCardForm from '../../components/account/AddCardForm';
import { useAccountAuth } from '../../contexts/AccountAuthContext';
import { API_BASE_URL } from '../../config/api';

const AccountPayment = () => {
  const { defaultPaymentMethod } = useAccountAuth();
  const [searchParams] = useSearchParams();
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const email = sessionStorage.getItem('account_email') || '';
  const pwd = sessionStorage.getItem('account_pwd') || '';

  useEffect(() => {
    const setupStatus = searchParams.get('setup');
    const redirectStatus = searchParams.get('redirect_status');
    if (setupStatus === 'success' || redirectStatus === 'succeeded') {
      confirmAndFetch();
    } else {
      fetchPaymentData();
    }
  }, []);

  const confirmAndFetch = async () => {
    setConfirming(true);
    try {
      await fetch(`${API_BASE_URL}/general/getgeneral`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: 'confirm', email, pwd })
      });
    } catch (err) { console.error('Error confirming payment:', err); }
    finally {
      setConfirming(false);
      setSuccessMessage('Payment method added successfully!');
      setTimeout(() => setSuccessMessage(null), 5000);
      await fetchPaymentData();
    }
  };

  const fetchPaymentData = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/general/getgeneral`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: 'sub', email, pwd })
      });
      const result = await response.json();
      if (result.success && result.data?.hpm) {
        setPaymentMethods([{ id: 'pm-default', brand: result.data.cb || 'card', last4: result.data.cl4 || '****', expMonth: result.data.cem, expYear: result.data.cey, isDefault: true }]);
      } else { setPaymentMethods([]); }
    } catch (error) { console.error('Error fetching payment data:', error); setPaymentMethods([]); }
    finally { setIsLoading(false); }
  };

  const handleCardAdded = () => { setShowAddForm(false); confirmAndFetch(); };

  const handleEditCard = (paymentMethod) => {
    // Edit = replace with a new card via the add form
    setShowAddForm(true);
  };

  const handleRemoveCard = async (id) => {
    if (!window.confirm('Are you sure you want to remove this payment method?')) return;
    setActionLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/general/getgeneral`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: 'confirm', email, pwd })
      });
      // Refresh after removal attempt
      await fetchPaymentData();
      setSuccessMessage('Payment method removed');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Error removing card:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const defaultMethod = paymentMethods.find(pm => pm.isDefault);

  if (isLoading || confirming) {
    return (<div className="min-h-screen bg-dark"><AccountHeader title="Payment Methods" /><div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 text-accent animate-spin mr-3" /><span className="text-gray-400">{confirming ? 'Saving payment method...' : 'Loading...'}</span></div></div>);
  }

  return (
    <div className="min-h-screen bg-dark">
      <AccountHeader title="Payment Methods" />
      <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {successMessage && (<motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="p-4 bg-accent/10 border border-accent/30 rounded-xl flex items-center gap-3"><CheckCircle className="w-5 h-5 text-accent shrink-0" /><p className="text-accent font-medium">{successMessage}</p></motion.div>)}
        {defaultMethod && (<motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}><h3 className="text-sm font-medium text-gray-400 mb-3">DEFAULT PAYMENT METHOD</h3><PaymentMethodCard paymentMethod={defaultMethod} isDefault={true} onEdit={handleEditCard} onRemove={handleRemoveCard} isLoading={actionLoading} /></motion.div>)}
        {paymentMethods.length === 0 && !showAddForm && (<motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center py-12"><div className="w-16 h-16 mx-auto mb-4 rounded-full bg-dark-100 flex items-center justify-center"><CreditCard className="w-8 h-8 text-gray-500" /></div><h3 className="text-lg font-medium text-white mb-2">No Payment Methods</h3><p className="text-gray-400 mb-6">Add a payment method to manage your subscription</p></motion.div>)}
        {showAddForm ? (<motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}><AddCardForm onSuccess={handleCardAdded} onCancel={() => setShowAddForm(false)} /></motion.div>) : (
          <motion.button initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} onClick={() => setShowAddForm(true)} className="w-full py-4 border border-dashed border-white/20 rounded-2xl text-gray-400 hover:text-white hover:border-accent/50 transition-all flex items-center justify-center gap-2"><Plus size={20} />{paymentMethods.length === 0 ? 'Add Payment Method' : 'Update Payment Method'}</motion.button>
        )}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="p-4 bg-dark-100 border border-white/10 rounded-xl"><div className="flex items-start gap-3"><AlertCircle className="w-5 h-5 text-gray-400 mt-0.5 shrink-0" /><div><p className="text-sm text-gray-300">Secure Payment Processing</p><p className="text-xs text-gray-500 mt-1">Your payment information is encrypted and securely processed through Stripe. We never store your full card number.</p></div></div></motion.div>
      </div>
    </div>
  );
};

export default AccountPayment;
